// src/utils/normalizeApiError.js
// Canlı maç API katmanı için merkezi hata normalize fonksiyonu.
// Amaç: component'lerin tek ve tutarlı bir hata şekliyle çalışması,
// kullanıcıya teknik stack trace göstermemek, retry kararını kolaylaştırmak.

const isDev =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV !== "production";

// Otomatik retry yapılabilecek HTTP durumları (geçici sunucu/ağ sorunları).
const RETRYABLE_STATUS = new Set([502, 503, 504]);

// Otomatik retry YAPILMAMASI gereken durumlar (kalıcı / mantıksal hatalar).
const NON_RETRYABLE_STATUS = new Set([400, 401, 403, 404, 409, 422]);

export const isAbortError = (error) => {
  if (!error) return false;
  return (
    error.name === "AbortError" ||
    error.name === "CanceledError" ||
    error.code === "ERR_CANCELED" ||
    String(error.message || "")
      .toLowerCase()
      .includes("aborted") ||
    String(error.message || "")
      .toLowerCase()
      .includes("canceled")
  );
};

const isNetworkError = (error) => {
  if (!error) return false;
  if (error.response) return false; // yanıt geldiyse ağ hatası değildir
  return (
    error.code === "ERR_NETWORK" ||
    error.code === "ECONNABORTED" ||
    error.message === "Network Error" ||
    error.message === "Failed to fetch"
  );
};

const isTimeoutError = (error) => {
  if (!error) return false;
  return (
    error.code === "ECONNABORTED" &&
    String(error.message || "").toLowerCase().includes("timeout")
  );
};

// Kullanıcıya gösterilecek sade mesajlar.
const messageForKind = (kind, status, serverMessage) => {
  switch (kind) {
    case "aborted":
      return "İşlem iptal edildi.";
    case "timeout":
      return "İşlem zaman aşımına uğradı.";
    case "network":
      return "Bağlantı kurulamadı.";
    case "duplicate":
      return "Bu olay daha önce kaydedilmiş.";
    case "validation":
      return serverMessage || "Gönderilen bilgiler geçersiz.";
    case "auth":
      return "Oturum doğrulanamadı.";
    case "forbidden":
      return "Bu işlem için yetkiniz yok.";
    case "notfound":
      return "Kayıt bulunamadı.";
    case "conflict":
      return "Maç durumu değişti, veriler yenileniyor.";
    case "server":
      return "Sunucu yoğun, işlem tekrar denenebilir.";
    default:
      return serverMessage || "Beklenmeyen bir hata oluştu.";
  }
};

const kindForStatus = (status) => {
  if (status === 409) return "conflict";
  if (status === 422 || status === 400) return "validation";
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notfound";
  if (RETRYABLE_STATUS.has(status)) return "server";
  if (status >= 500) return "server";
  return "unknown";
};

/**
 * Axios/fetch hatasını tek tip bir yapıya normalize eder.
 * @param {any} error
 * @returns {{
 *   ok: false,
 *   kind: string,
 *   status: number|null,
 *   message: string,
 *   retryable: boolean,
 *   duplicate: boolean,
 *   serverData: any,
 *   original: any
 * }}
 */
export function normalizeApiError(error) {
  if (isAbortError(error)) {
    return {
      ok: false,
      kind: "aborted",
      status: null,
      message: messageForKind("aborted"),
      retryable: false,
      duplicate: false,
      serverData: null,
      original: error,
    };
  }

  if (isTimeoutError(error)) {
    return {
      ok: false,
      kind: "timeout",
      status: null,
      message: messageForKind("timeout"),
      retryable: true, // timeout güvenli retry adayı (aynı client_event_id ile)
      duplicate: false,
      serverData: null,
      original: error,
    };
  }

  if (isNetworkError(error)) {
    return {
      ok: false,
      kind: "network",
      status: null,
      message: messageForKind("network"),
      retryable: true,
      duplicate: false,
      serverData: null,
      original: error,
    };
  }

  const status = error?.response?.status ?? null;
  const serverData = error?.response?.data ?? null;
  const serverMessage =
    (serverData &&
      (serverData.message ||
        serverData.error ||
        serverData.detail ||
        serverData.hata)) ||
    "";

  // Backend duplicate'i 409 ile bildirebilir. Çağıran taraf bunu
  // "başarı" olarak normalize etmek isteyebilir; burada işaretliyoruz.
  const duplicate =
    status === 409 &&
    Boolean(
      serverData &&
        (serverData.duplicate === true ||
          serverData.already_exists === true ||
          serverData.code === "DUPLICATE")
    );

  const kind = duplicate ? "duplicate" : kindForStatus(status);
  const retryable = status != null ? RETRYABLE_STATUS.has(status) : false;

  return {
    ok: false,
    kind,
    status,
    message: messageForKind(kind, status, serverMessage),
    retryable,
    duplicate,
    serverData,
    original: error,
  };
}

/**
 * Geliştirme ortamında güvenli log; production'da gürültü yapmaz.
 */
export function logApiError(scope, normalized) {
  if (!isDev) return;
  // Büyük objeler yerine sadeleştirilmiş bilgi.
  // eslint-disable-next-line no-console
  console.warn(
    `[liveMatch] ${scope}: ${normalized?.kind || "error"}` +
      (normalized?.status ? ` (${normalized.status})` : "") +
      ` -> ${normalized?.message || ""}`
  );
}

export const RETRYABLE_HTTP_STATUS = RETRYABLE_STATUS;
export const NON_RETRYABLE_HTTP_STATUS = NON_RETRYABLE_STATUS;

export default normalizeApiError;
